/**
 * mymemo API — /api/onto/memo*. 개인 메모(세번 메모·업무 메모)와 첨부 파일.
 * 인증은 로그인 JWT(Bearer). ★(세번 즐겨찾기)는 watchlistApi 가 그대로 맡는다.
 */
function token() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}

function headers(json = false) {
  const h = {};
  const t = token();
  if (t) h.Authorization = `Bearer ${t}`;
  if (json) h["Content-Type"] = "application/json";
  return h;
}

async function request(url, options = {}) {
  const r = await fetch(url, {
    ...options,
    headers: { ...headers(typeof options.body === "string"), ...(options.headers || {}) },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    if (r.status === 401) throw new Error("로그인이 끝났습니다. 다시 로그인해 주세요.");
    if (r.status === 413 && !body.error) throw new Error("파일이 너무 큽니다. 25MB 이하로 올려 주세요.");
    throw new Error(body.error || `메모 요청에 실패했습니다. (${r.status})`);
  }
  return body;
}

export function memoLoggedIn() {
  return Boolean(token());
}

export function listMemos() {
  return request("/api/onto/memo");
}

export function createMemo(memo) {
  return request("/api/onto/memo", { method: "POST", body: JSON.stringify(memo) });
}

export function updateMemo(id, memo) {
  return request(`/api/onto/memo/${id}`, { method: "PUT", body: JSON.stringify(memo) });
}

export function deleteMemo(id) {
  return request(`/api/onto/memo/${id}`, { method: "DELETE" });
}

export function uploadMemoAttachment(id, file) {
  const form = new FormData();
  form.append("file", file);
  return request(`/api/onto/memo/${id}/attachments`, { method: "POST", body: form });
}

export function deleteMemoAttachment(id, aid) {
  return request(`/api/onto/memo/${id}/attachments/${aid}`, { method: "DELETE" });
}

/** 첨부를 받아 blob URL 로 — 화면 안 보기·저장 둘 다 이것을 쓴다. 쓴 뒤 URL.revokeObjectURL. */
export async function fetchMemoAttachment(id, aid) {
  const r = await fetch(`/api/onto/memo/${id}/attachments/${aid}`, { headers: headers() });
  if (!r.ok) throw new Error(r.status === 404 ? "파일을 찾을 수 없습니다." : `파일을 열지 못했습니다. (${r.status})`);
  const blob = await r.blob();
  return URL.createObjectURL(blob);
}

export const MAX_FILE_BYTES = 25 * 1024 * 1024;

/** 메모 상태 — 검색 결론(옛 품목 관리)과 같은 값. 화면에서는 태그처럼 보인다. */
export const STATUS = { reviewing: "검토 중", conditional: "추가 확인", confirmed: "확정" };

/**
 * 통관 업무 항목 — 세번 밖의 일상 업무 메모로 넓히는 자리. 서버 MemoInput.FIELD_KEYS 와 같은 키.
 * 항목을 늘릴 때는 두 곳에 함께 더한다.
 */
export const MEMO_FIELDS = [
  { key: "decl_no", label: "신고번호", placeholder: "예: 13225-26-0012345" },
  { key: "partner", label: "거래처", placeholder: "화주·해외 공급자" },
  { key: "customs", label: "세관", placeholder: "예: 인천세관" },
  { key: "due", label: "기한", type: "date" },
];

/** 태그 고르기 도우미 — 자주 쓰는 통관 업무 분류(내가 쓴 태그가 앞에 온다). */
export const SUGGESTED_TAGS = ["감면", "FTA", "원산지", "요건", "환급", "사전심사", "세관의견", "수입", "수출", "정정"];
export const ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp,.doc,.docx,.xls,.xlsx,.csv,.txt,.hwp,.hwpx,.ppt,.pptx,.eml,.msg";

export function formatBytes(n) {
  const v = Number(n) || 0;
  if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(1)}GB`;
  if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1)}MB`;
  if (v >= 1024) return `${Math.round(v / 1024)}KB`;
  return `${v}B`;
}

export function formatHsk(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length >= 10) return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6, 10)}`;
  if (d.length >= 6) return `${d.slice(0, 4)}.${d.slice(4, 6)}`;
  return d;
}

/** 본문 속 세번(0000.00 / 0000.00-0000 / 10자리) 위치 — 자동 링크용. */
export function findHskMentions(text) {
  const out = [];
  // 앞쪽 경계는 lookbehind 대신 캡처로 — 옛 사파리가 lookbehind 를 못 읽는다.
  const re = /(^|[^\d.])(\d{4}\.\d{2}(?:-\d{4})?|\d{10})(?!\d|\.\d)/g; // 날짜(2026.05.14) 제외
  let m;
  const s = String(text || "");
  while ((m = re.exec(s))) {
    const start = m.index + m[1].length;
    out.push({ start, end: start + m[2].length, code: m[2].replace(/\D/g, "") });
  }
  return out;
}

/** 그리드 한 줄 — 메모·★만 단 세번·검색 결론(품목 관리)을 같은 모양으로. */
export function buildRows({ memos = [], stars = [], workspaces = [] }) {
  const rows = [];
  const memoHsk = new Set();
  memos.forEach((m) => {
    if (m.hsk) memoHsk.add(m.hsk);
    rows.push({
      key: `m${m.id}`,
      source: "memo",
      id: m.id,
      kind: m.kind,
      hsk: m.hsk || "",
      title: m.title || firstLine(m.body) || (m.hsk ? "" : "제목 없음"),
      body: m.body || "",
      tags: m.tags || [],
      status: m.status || "",
      systemTags: [m.status && STATUS[m.status]].filter(Boolean),
      pinned: Boolean(m.pinned),
      fields: m.fields || {},
      attachments: m.attachments || [],
      updatedAt: m.updated_at,
      createdAt: m.created_at,
      memo: m,
    });
  });
  stars.forEach((s) => {
    if (memoHsk.has(s.code)) return;
    rows.push({
      key: `s${s.code}`,
      source: "star",
      id: null,
      kind: "hs",
      hsk: s.code,
      title: s.alias || s.name || "",
      body: "",
      tags: [],
      status: "",
      systemTags: [],
      pinned: false,
      fields: {},
      attachments: [],
      updatedAt: s.created_at,
      createdAt: s.created_at,
    });
  });
  const memoWs = new Set(memos.map((m) => m.workspace_id).filter(Boolean));
  workspaces.forEach((w) => {
    if (!w.workspaceId || memoWs.has(w.workspaceId)) return;
    rows.push({
      key: `w${w.workspaceId}`,
      source: "search",
      id: null,
      kind: "hs",
      hsk: String(w.recommendedHsk || "").replace(/\D/g, ""),
      title: w.title || w.queryText || "",
      body: "",
      tags: [],
      status: w.status || "reviewing",
      systemTags: ["검색 결론", STATUS[w.status] || STATUS.reviewing],
      pinned: false,
      fields: {},
      attachments: [],
      evidenceCount: Number(w.evidenceCount || 0) + Number(w.noteCount || 0),
      updatedAt: w.updatedAt,
      createdAt: w.updatedAt,
      workspace: w,
    });
  });
  return rows;
}

/** 본문 속 http(s) 주소 — 끝의 문장부호는 뗀다. */
export function findUrls(text) {
  const out = [];
  const re = /https?:\/\/[^\s<>"'()]+/g;
  let m;
  const s = String(text || "");
  while ((m = re.exec(s))) {
    const u = m[0].replace(/[.,;:!?)\]]+$/, "");
    if (!out.includes(u)) out.push(u);
  }
  return out;
}

/** AI로 정리 — {summary, organized}. 저장하지 않는다. */
export function tidyMemo(text) {
  return request("/api/onto/memo/tidy", { method: "POST", body: JSON.stringify({ text }) });
}

export function firstLine(text) {
  const line = String(text || "").split(/\r?\n/).find((l) => l.trim());
  return line ? line.trim().slice(0, 120) : "";
}

/** 검색어로 거르기 — 제목·본문·세번(점·줄 없이도)·태그·첨부 파일명. */
export function matchesQuery(row, query) {
  const q = String(query || "").trim().toLocaleLowerCase();
  if (!q) return true;
  const digits = q.replace(/[.\-\s]/g, "");
  const hay = [row.title, row.body, row.tags.join(" "), (row.systemTags || []).join(" "),
    Object.values(row.fields || {}).join(" "), row.attachments.map((a) => a.file_name).join(" ")]
    .join("\n")
    .toLocaleLowerCase();
  if (q.split(/\s+/).every((w) => hay.includes(w))) return true;
  return /^\d{2,10}$/.test(digits) && row.hsk.startsWith(digits);
}
